import React, { useRef } from "react";
import { gsap } from "gsap";
import { DrawSVGPlugin } from "gsap/dist/DrawSVGPlugin";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Box } from "@chakra-ui/react";
import { registerGsapPlugins } from "lib/gsap";
import { StepThreeSvg } from "./step-3-svg";

registerGsapPlugins(ScrollTrigger, DrawSVGPlugin);

export function StepThreeAnimation() {
  const ref = useRef<HTMLDivElement>(null);
  const rotationTimeline = useRef<GSAPTimeline>();
  const sequenceTimeline = useRef<GSAPTimeline>();

  useGSAP(
    () => {
      // Infinite circle rotation
      rotationTimeline.current = gsap.timeline({
        delay: 0.25,
        scrollTrigger: {
          trigger: "#aid-step-3-svg",
          onEnter: () => rotationTimeline.current?.restart(true),
          onLeave: () => rotationTimeline.current?.pause(),
          onEnterBack: () => rotationTimeline.current?.restart(true),
          onLeaveBack: () => rotationTimeline.current?.pause(),
        },
      });
      rotationTimeline.current.to("#step-3-circle", {
        rotation: "+=360",
        transformOrigin: "50% 50%",
        duration: 3,
        ease: "power1.in",
      });
      rotationTimeline.current.to("#step-3-circle", {
        rotation: "+=360",
        transformOrigin: "50% 50%",
        duration: 1.5,
        ease: "linear",
        repeat: -1,
      });

      // Product viewed → purchased sequence
      sequenceTimeline.current = gsap.timeline({
        delay: 1.5,
        repeat: -1,
        defaults: {
          duration: 0.7,
          ease: "power3.inOut",
        },
        scrollTrigger: {
          trigger: "#aid-step-3-svg",
          onEnter: () => sequenceTimeline.current?.restart(true),
          onLeave: () => sequenceTimeline.current?.pause(),
          onEnterBack: () => sequenceTimeline.current?.restart(true),
          onLeaveBack: () => sequenceTimeline.current?.pause(),
        },
      });
      sequenceTimeline.current
        // Product viewed
        .from("#product-viewed-indicator", {
          opacity: 0,
        })
        .from("#product-viewed-box", {
          duration: 0.8,
          drawSVG: "0",
        })
        .from(
          "#product-viewed-box",
          {
            duration: 0.5,
            fillOpacity: 0,
          },
          "<",
        )
        .from(
          "#product-viewed-text",
          {
            opacity: 0,
            duration: 0.7,
          },
          "-=0.5",
        )
        .from("#product-viewed-line", {
          duration: 1.2,
          drawSVG: "0",
        })
        .from(
          ".product-viewed-dots",
          {
            duration: 0.4,
            stagger: 0.1,
            fill: "var(--chakra-colors-new-violet-900)",
          },
          "<",
        )
        // Reset product viewed
        .to("#product-viewed-box", { opacity: 0 }, "+=2")
        .to("#product-viewed-text", { opacity: 0 }, "<")
        .to(
          "#product-viewed-line",
          {
            duration: 1,
            drawSVG: "0%",
          },
          "<",
        )
        .to(
          ".product-viewed-dots",
          {
            fill: "var(--chakra-colors-new-violet-900)",
          },
          "<",
        )
        .to(
          "#product-viewed-indicator",
          {
            opacity: 0,
          },
          "-=0.8",
        )
        // Purchased sequence
        .from("#purchased-line", {
          duration: 1.2,
          drawSVG: "all",
        })
        .from(
          ".purchased-dots",
          {
            duration: 0.4,
            opacity: 0,
            stagger: 0.1,
          },
          "-=0.7",
        )
        .from(
          "#purchased-indicator",
          {
            duration: 0.4,
            opacity: 0,
          },
          "<+0.3",
        )
        .from(
          "#purchased-box",
          {
            duration: 0.8,
            drawSVG: "0",
          },
          "-=0.5",
        )
        .from(
          "#purchased-box",
          {
            duration: 0.5,
            fillOpacity: 0,
          },
          "<",
        )
        .from(
          "#purchased-text",
          {
            opacity: 0,
            duration: 0.7,
          },
          "-=0.5",
        )
        // Reset purchased
        .to("#purchased-box", { opacity: 0 }, "+=2.5")
        .to("#purchased-text", { opacity: 0 }, "<")
        .to(
          "#purchased-line",
          {
            duration: 1,
            drawSVG: "0%",
          },
          "<",
        )
        .to(
          ".purchased-dots",
          {
            fill: "var(--chakra-colors-new-violet-900)",
          },
          "<",
        )
        .to(
          "#purchased-indicator",
          {
            opacity: 0,
          },
          "-=0.8",
        );
    },
    { scope: ref },
  );

  return (
    <Box ref={ref} boxSize="100%" pos="relative">
      <StepThreeSvg id="aid-step-3-svg" boxSize="100%" />
    </Box>
  );
}
